<template>
  <div button-accept="false" button-cancel="false" :active.sync="isActive">
    <div class="mb-4 flex items-center">
      <vs-button @click="isActive = false" color="dark" type="filled" icon-pack="feather" icon="icon-arrow-left"> Back </vs-button>
    </div>
    <div class="vx-col w-full md:w-1/3 px-4">
      <form>
        <!-- Event Title -->
        <div class="vx-row mb-3">
          <vs-input
            icon-pack="feather"
            class="w-full"
            v-validate="'exclude_special_char'"
            v-model="form.event_title"
            :label="`Event Title`"
            placeholder="Enter the event title, e.g., Broadcasting, Aarti..."
            name="title"
            id="title"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('title')">{{ errors.first('title') }}</span>
        </div>
        <div class="vx-row mb-3">
          <label for="fromDate" style="font-size: 14px; font-weight: bold">From Date & Time *</label>
          <flat-pickr
            v-model="form.from_date"
            :config="datePickerConfig"
            class="w-full"
            placeholder="Select From Date & Time"
            id="fromDate"
            name="fromDate"
            data-vv-as="From Date & Time"
            v-validate="'required'"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('fromDate')">{{ errors.first('fromDate') }}</span>
        </div>
        <div class="vx-row mb-3">
          <label for="toDate" style="font-size: 14px; font-weight: bold">To Date & Time *</label>
          <flat-pickr
            v-model="form.to_date"
            :config="datePickerConfig"
            class="w-full"
            placeholder="Select To Date & Time"
            id="toDate"
            name="toDate"
            data-vv-as="To Date & Time"
            v-validate="'required'"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('toDate')">{{ errors.first('toDate') }}</span>
          <span class="text-danger text-sm" v-show="dateError">{{ dateError }}</span>
        </div>

        <!-- Time Picker for From Time and To Time -->
        <!-- <div class="vx-row mb-3">
          <label for="fromTime" style="font-size: 14px; font-weight: bold">From Time *</label>
          <flat-pickr
            v-model="form.from_time"
            :config="{ enableTime: true, noCalendar: true, dateFormat: 'h:i K' }"
            placeholder="Select From Time"
            class="w-full"
            id="fromTime"
            data-vv-as="From Time"
            name="fromTime"
            v-validate="'required'"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('fromTime')">{{ errors.first('fromTime') }}</span>
        </div>

        <div class="vx-row mb-3">
          <label for="toTime" style="font-size: 14px; font-weight: bold">To Time *</label>
          <flat-pickr
            v-model="form.to_time"
            :config="{ enableTime: true, noCalendar: true, dateFormat: 'h:i K' }"
            placeholder="Select To Time"
            class="w-full"
            id="toTime"
            data-vv-as="To Time"
            name="toTime"
            v-validate="'required'"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('toTime')">{{ errors.first('toTime') }}</span>
          <span class="text-danger text-sm" v-show="timeError">{{ timeError }}</span>
        </div> -->

        <!-- User -->
        <div class="vx-row mb-3">
          <label class="vs-input--label">User *</label>
          <customUser-select-2
            class="w-full user-input"
            name="user"
            placeholder=" Select User"
            :value="form.user"
            @input="(item) => (form.user = item && item.value)"
            autocomplete
            :ssr="true"
            :multiple="false"
            v-validate="'required'"
            data-vv-as="User"
            action="common/getUsers"
            :viewRendered="false"
            :options="UserList"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('user')">{{ errors.first('user') }}</span>
        </div>

        <!-- YouTube Live Link -->
        <div class="vx-row mb-3">
          <vs-input
            v-model="form.youtube_live_link"
            class="w-full"
            v-validate="'url'"
            :label="`YouTube Live Link`"
            placeholder="Enter YouTube live stream link"
            name="youtube_live_link"
            id="youtube_live_link"
            data-vv-as="Youtube Live Link"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('youtube_live_link')">{{ errors.first('youtube_live_link') }}</span>
        </div>

        <!-- Facebook Live Link -->
        <div class="vx-row mb-3">
          <vs-input
            v-model="form.facebook_live_link"
            class="w-full"
            v-validate="'url'"
            :label="`Facebook Live Link`"
            placeholder="Enter Facebook live stream link"
            name="facebook_live_link"
            id="facebook_live_link"
            data-vv-as="Facebook Live Link"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('facebook_live_link')">{{ errors.first('facebook_live_link') }}</span>
        </div>

        <!-- New Field: Visibility -->
        <div class="vx-row mb-3">
          <label class="vs-input--label">Visibility *</label>
          <select-2
            class="w-full"
            :name="'visibility'"
            placeholder="Select Visibility"
            :options="[
              { value: 'Public', label: 'Public' },
              { value: 'Private', label: 'Private' }
            ]"
            autocomplete
            :ssr="true"
            v-validate="'required'"
            :multiple="false"
            :clearable="false"
            :value="form.visibility"
            data-vv-as="Visibility"
            @input="(item) => (form.visibility = item && item.value)"
            :disabled="!isEditable"
          />
          <span class="text-danger text-sm" v-show="errors.has('visibility')">
            {{ errors.first('visibility') }}
          </span>
        </div>

        <!-- Mobile Numbers Section -->
        <div class="vx-row mb-3">
          <div v-for="(mobile, index) in form.mobile_number" :key="index" class="vx-col w-full px-2 mb-4">
            <div class="flex items-center">
              <div class="w-full">
                <label class="vs-input--label">Mobile Number *</label>
                <vue-tel-input
                  v-model="form.mobile_number[index].full_number"
                  class="w-full vs-inputx vs-input--input"
                  placeholder="Enter mobile number"
                  v-validate="'required'"
                  data-vv-as="Mobile Number"
                  :name="'mobile_number_' + index"
                  :disabled="!isEditable"
                  @input="(value, metadata) => updateMobileNumber(index, value, metadata)"
                />
                <span class="text-danger text-sm" v-show="errors.has(`mobile_number_${index}`)">{{ errors.first(`mobile_number_${index}`) }}</span>
              </div>
              <vx-tooltip :text="`Remove Mobile Number`" v-if="isEditable">
                <vs-button @click="removeMobileNumber(index)" class="mt-6 ml-2" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2" />
              </vx-tooltip>
            </div>
          </div>
          <vs-button @click="addMobileNumber()" color="success" size="default"> Add Mobile Number</vs-button>
        </div>

        <!-- Save & Cancel Buttons -->
        <div class="vx-row mt-6">
          <div>
            <vs-button class="mr-2 vs-con-loading__container" id="create-contact" @click="save_changes" v-if="isEditable">Save Changes</vs-button>
            <vs-button color="danger" @click="navigateList" class="text-white-500"> Cancel </vs-button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Select2 from '@/components/custom/form-elements/Select2.vue'
import { dateMixins } from '@/mixins/dateMixins.js'
import { mapActions, mapGetters, mapState } from 'vuex'
import CustomUserSelect2 from '@/components/custom/form-elements/CustomUserSelect2.vue'
import 'flatpickr/dist/flatpickr.css'
import moment from 'moment'
import flatPickr from 'vue-flatpickr-component'
export default {
  /** Component Name */
  name: 'AddUpdateRagi',

  /** Props */
  props: {
    selectedRecordId: String,
    module_name: String,
    action_name: String,
    showModal: Boolean
  },

  components: {
    Select2,
    flatPickr,
    CustomUserSelect2
  },

  /** Data */
  data() {
    return {
      form: {
        event_title: '',
        // dateRange: null,
        from_date: null,
        to_date: null,
        gurudwara: this.$route.params.id,
        user: null,
        youtube_live_link: '',
        facebook_live_link: '',
        visibility: 'Public',
        mobile_number: []
      },
      dateError: null,
      isEditing: !!this.selectedRecordId,
      UserList: []
    }
  },

  // Date Formate.
  mixins: [dateMixins],

  /** Computed Properties */
  computed: {
    ...mapState('gurudwara', ['createLoading']),
    ...mapGetters('auth', ['checkPermissionSlug']),
    isEditable() {
      return this.checkPermissionSlug('gurudwara_ragi_edit')
    },
    validateForm() {
      return !this.errors.any() && !this.dateError
    },
    isActive: {
      get() {
        return this.showModal
      },
      set(val) {
        this.$emit('update:showModal', val)
      }
    },
    datePickerConfig() {
      return {
        enableTime: true,
        // mode: this.isEditing ? 'single' : 'range',
        dateFormat: 'd-m-Y h:i K'
      }
    }
  },

  /** Lifecycle Hooks */
  async created() {
    if (this.selectedRecordId) {
      try {
        const res = await this.$store.dispatch('gurudwara/ragiShow', this.selectedRecordId)
        const data = res.data.data

        /* Initialize form with fetched data */
        this.form.event_title = data.event_title || ''
        this.form.user = data.user
        this.form.youtube_live_link = data.youtube_live_link || ''
        this.form.facebook_live_link = data.facebook_live_link || ''
        this.form.visibility = data.visibility || ''

        this.form.mobile_number = (data.mobile_number || []).map((mobile) => ({
          country_code: mobile.country_code,
          mobile_number: mobile.mobile_number,
          full_number: mobile.country_code ? `+${mobile.country_code} ${mobile.mobile_number}` : mobile.mobile_number
        }))
        const fromDate = this.formatLocal(data.from_date, 'DD-MM-YYYY')
        this.form.dateRange = fromDate

        this.form.from_date = this.formatLocal(data.from_date, 'DD-MM-YYYY h:mm A')
        this.form.to_date = this.formatLocal(data.to_date, 'DD-MM-YYYY h:mm A')
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: error.message || 'Failed to load ragi details.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
      }
    }

    const userResult = await this.getUsers({ page: 1, limit: 300000, type: 'dropdown' })
    this.UserList = userResult.data

    // user_id: data.user._id
  },

  /** Methods */
  methods: {
    ...mapActions('gurudwara', {
      create: 'ragiCreate',
      update: 'ragiUpdate'
    }),
    ...mapActions('common', {
      getUsers: 'getUsers'
    }),
    addMobileNumber() {
      this.form.mobile_number.push({
        country_code: '',
        mobile_number: ''
      })
    },

    updateMobileNumber(index, value, metadata) {
      if (this.form.mobile_number[index]) {
        this.form.mobile_number[index].mobile_number = metadata.nationalNumber
        this.form.mobile_number[index].country_code = metadata.countryCallingCode
        this.$set(this.form.mobile_number[index], 'full_number', value)
      }
    },

    removeMobileNumber(mobileIndex) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Delete`,
        text: `Are you sure you want to delete?`,
        accept: () => {
          this.form.mobile_number.splice(mobileIndex, 1)
        },
        acceptText: 'Delete'
      })
    },

    navigateList() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Cancel`,
        text: `Are you sure you want to cancel?`,
        accept: () => {
          this.isActive = false
        },
        acceptText: 'Yes',
        cancelText: 'No'
      })
    },
    /**
     * Save Changes Handler
     */
    async save_changes() {
      // Validate the entire form
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please fix the errors first.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
        return
      }

      const fromDate = moment(this.form.from_date, 'DD-MM-YYYY h:mm A')
      const toDate = moment(this.form.to_date, 'DD-MM-YYYY h:mm A')

      if (this.isEditing) {
        const payload = {
          event_title: this.form.event_title,
          from_date: fromDate,
          user: this.form.user,
          to_date: toDate,
          youtube_live_link: this.form.youtube_live_link,
          facebook_live_link: this.form.facebook_live_link,
          visibility: this.form.visibility,
          mobile_number: this.form.mobile_number.map((mobile) => ({
            country_code: mobile.country_code,
            mobile_number: mobile.mobile_number
          }))
        }

        try {
          // Update existing Ragi
          const response = await this.update({
            id: this.selectedRecordId,
            data: payload
          })

          // Close modal and notify success
          this.isActive = false
          this.$emit('update-data', true)
          this.$vs.notify({
            title: 'Success',
            text: response.message || 'Ragi updated successfully.',
            iconPack: 'feather',
            icon: 'icon-check',
            position: 'top-center',
            time: 5000,
            color: 'success'
          })
        } catch (error) {
          // Notify error
          this.$vs.notify({
            title: 'Error',
            text: error.message || 'An error occurred while updating.',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            position: 'top-center',
            time: 5000,
            color: 'primary'
          })
        }
      } else {
        const payload = {
          gurudwara: this.form.gurudwara,
          event_title: this.form.event_title,
          from_date: fromDate,
          user: this.form.user,
          to_date: toDate,
          youtube_live_link: this.form.youtube_live_link,
          facebook_live_link: this.form.facebook_live_link,
          visibility: this.form.visibility,
          mobile_number: this.form.mobile_number.map((mobile) => ({
            country_code: mobile.country_code,
            mobile_number: mobile.mobile_number
          }))
        }
        try {
          // Create new Ragi
          const response = await this.create(payload)

          // Close modal and notify success
          this.isActive = false
          this.$emit('update-data', true)
          this.$vs.notify({
            title: 'Success',
            text: response.message || 'Ragi created successfully.',
            iconPack: 'feather',
            icon: 'icon-check',
            position: 'top-center',
            time: 5000,
            color: 'success'
          })
        } catch (error) {
          // Notify error
          this.$vs.notify({
            title: 'Error',
            text: error.message || 'An error occurred while creating.',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            position: 'top-center',
            time: 5000,
            color: 'primary'
          })
        }
      }
    },

    validateDateRange() {
      if (this.form.from_date && this.form.to_date) {
        const fromDate = moment(this.form.from_date, 'DD-MM-YYYY h:mm A')
        const toDate = moment(this.form.to_date, 'DD-MM-YYYY h:mm A')

        if (fromDate.isSameOrAfter(toDate)) {
          this.dateError = 'From Date must be earlier than To Date.'
        } else {
          this.dateError = null
        }
      }
    }
  },

  /** Watchers */
  watch: {
    'form.from_date': 'validateDateRange',
    'form.to_date': 'validateDateRange',
    createLoading(newVal) {
      if (newVal) {
        this.$vs.loading({
          container: '#create-ragi',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#create-ragi > .con-vs-loading')
      }
    }
  }
}
</script>
